// src/features/academic/pages/AcademicChallenge.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Trophy, Brain, Medal, Award } from 'lucide-react';
import { Toaster } from 'react-hot-toast';

// Components
import DailyQuiz from '../components/DailyQuiz';
import Leaderboard from '../components/Leaderboard';
import UserScore from '../components/UserScore';
import QuizRules from '../components/QuizRules';
import UniversityRankingItem from '../components/UniversityRankingItem';

// Hooks and Services
import useAcademicChallenge from '../hooks/useAcademicChallenge';
import challengeService from '../services/challengeService';
import { useAuth } from '../../../core/contexts/AuthContext';

/**
 * Academic Challenge Page
 * Main page for the daily academic challenge feature
 */
const AcademicChallengePage = () => {
  const navigate = useNavigate();
  const { currentUser, userProfile } = useAuth();
  const [leaderboard, setLeaderboard] = useState([]);
  const [universityRankings, setUniversityRankings] = useState([]);
  const [loadingLeaderboard, setLoadingLeaderboard] = useState(true);
  const [loadingUniversities, setLoadingUniversities] = useState(true);
  const [userRank, setUserRank] = useState(null);
  const [totalScore, setTotalScore] = useState(0);
  const [showFullRules, setShowFullRules] = useState(false);
  
  // Get quiz state and functions from custom hook
  const quizState = useAcademicChallenge();
  
  // Destructure the function handlers we need
  const {
    startQuiz,
    handleExitQuiz,
    handleSelectAnswer,
    submitAnswer,
    handleNextQuestion,
    setShowRules,
    fetchUserTotalScore
  } = quizState;
  
  // Fetch leaderboard data
  useEffect(() => {
    const fetchLeaderboard = async () => {
      try {
        setLoadingLeaderboard(true);
        const data = await challengeService.fetchLeaderboard(15);
        setLeaderboard(data);
        
        // Find user's rank if available
        if (currentUser) {
          const userEntry = data.find(entry => entry.userId === currentUser.uid);
          if (userEntry) {
            setUserRank(userEntry.rank);
          } else {
            // If not in top 15, get actual rank
            const userRankPosition = await challengeService.getUserRankingPosition(currentUser.uid);
            if (userRankPosition) {
              setUserRank(userRankPosition);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching leaderboard:', error);
      } finally {
        setLoadingLeaderboard(false);
      }
    };
    
    fetchLeaderboard();
  }, [currentUser, quizState.hasCompletedToday, quizState.userScore]);
  
  // Fetch university rankings
  useEffect(() => {
    const fetchUniversityRankings = async () => {
      try {
        setLoadingUniversities(true);
        const data = await challengeService.fetchUniversityRankings(5);
        setUniversityRankings(data);
      } catch (error) {
        console.error('Error fetching university rankings:', error);
      } finally {
        setLoadingUniversities(false);
      }
    };
    
    fetchUniversityRankings();
  }, [quizState.hasCompletedToday]);
  
  // Fetch user's total score
  useEffect(() => {
    const fetchTotalScore = async () => {
      if (!currentUser) return;
      
      try {
        const score = await fetchUserTotalScore();
        setTotalScore(score);
      } catch (error) {
        console.error('Error fetching total score:', error);
      }
    };
    
    fetchTotalScore();
  }, [currentUser, fetchUserTotalScore, quizState.hasCompletedToday]);
  
  // Handle navigation back
  const handleBack = () => {
    navigate('/home');
  };
  
  // Show full rules modal
  const handleShowFullRules = () => {
    setShowFullRules(true);
  };
  
  // Hide full rules modal
  const handleHideFullRules = () => {
    setShowFullRules(false);
  };
  
  // Get user's university
  const userUniversity = userProfile?.academicInfo?.university || null;
  
  return (
    <div className="min-h-screen bg-gray-50 pb-16">
      {/* Toast notifications */}
      <Toaster position="top-right" />
      
      {/* Full Rules Modal */}
      <AnimatePresence>
        {showFullRules && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 z-40 flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="w-full max-w-lg"
            >
              <QuizRules onCancel={handleHideFullRules} onStart={startQuiz} />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 py-6 pt-16">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <button 
            onClick={handleBack}
            className="text-gray-600 hover:text-gray-800 flex items-center"
          >
            <ArrowLeft size={20} className="mr-1" />
            <span>Back</span>
          </button>
          
          <h1 className="text-2xl font-bold text-center text-gray-800 flex-1 flex items-center justify-center">
            <Trophy className="mr-2 text-green-500" size={24} />
            Academic Challenge
          </h1>
          
          <div className="w-20"></div> {/* Empty space for balance */}
        </div>
        
        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Left Column - Quiz */}
          <div className="lg:col-span-8 space-y-6">
            {/* Quiz Card */}
            <DailyQuiz
              quizState={quizState}
              onStart={startQuiz}
              onExit={handleExitQuiz}
              onSelectAnswer={handleSelectAnswer}
              onSubmitAnswer={submitAnswer}
              onNextQuestion={handleNextQuestion}
              onShowRules={handleShowFullRules}
              onHideRules={() => setShowRules(false)}
            />
            
            {/* Fun Facts Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-xl shadow-md p-6"
            >
              <div className="flex items-start">
                <div className="p-3 bg-blue-100 rounded-lg mr-4">
                  <Brain className="text-blue-600" size={24} />
                </div>
                <div>
                  <h3 className="font-bold text-gray-800 mb-1">Did You Know?</h3>
                  <p className="text-gray-600 text-sm">
                    Students who regularly participate in academic challenges have been shown to improve 
                    their knowledge retention by up to 30% compared to traditional study methods.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
          
          {/* Right Column - User Stats & Leaderboard */}
          <div className="lg:col-span-4 space-y-6">
            {/* User Score Card */}
            <UserScore
              todayScore={quizState.userScore}
              totalScore={totalScore}
              rank={userRank}
              hasCompletedToday={quizState.hasCompletedToday}
              userName={userProfile?.basicInfo?.name}
              userAvatar={userProfile?.photos?.[0]}
              streakCount={0} // Would need streak tracking in the backend
              university={userUniversity}
            />
            
            {/* Leaderboard */}
            <Leaderboard
              entries={leaderboard}
              loading={loadingLeaderboard}
              currentUserId={currentUser?.uid}
              title="Challenge Leaderboard"
            />
          </div>
        </div>
        
        {/* University Rankings Section */}
        <div className="mt-8">
          <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
            <Medal className="mr-2 text-amber-500" size={20} />
            University Rankings
          </h2>
          
          <div className="bg-white rounded-xl shadow-md overflow-hidden">
            <div className="p-4 bg-gradient-to-r from-amber-500 to-amber-600 text-white">
              <h3 className="font-semibold">Top Universities by Challenge Points</h3>
            </div>
            
            <div className="p-4">
              {loadingUniversities ? (
                <div className="text-center py-4">
                  <div className="inline-block animate-spin w-6 h-6 border-2 border-green-500 border-t-transparent rounded-full"></div>
                  <p className="text-gray-500 mt-2">Loading university rankings...</p>
                </div>
              ) : universityRankings.length === 0 ? (
                <div className="text-center py-4">
                  <p className="text-gray-500">No university rankings available yet.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {universityRankings.map(university => (
                    <UniversityRankingItem
                      key={university.id}
                      rank={university.rank}
                      name={university.name}
                      score={university.totalScore}
                      participants={university.participants}
                      isHighlighted={userUniversity === university.name}
                    />
                  ))}
                </div>
              )}
              
              {universityRankings.length > 0 && (
                <div className="mt-4 text-center">
                  <button
                    onClick={() => navigate('/academic/leaderboard')}
                    className="text-sm text-green-600 hover:text-green-700 font-medium"
                  >
                    View Full Rankings
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
        
       
      </div>
    </div>
  );
};

export default AcademicChallengePage;